export type GameSource = 'lichess' | 'twic';

export type ExtendedGameSource =
  | GameSource
  | 'lichess_user'
  | 'chesscom_user'
  | 'unknown';

export const CORPUS_GAME_SOURCES: GameSource[] = ['lichess', 'twic'];

export const ALL_GAME_SOURCES: GameSource[] = [...CORPUS_GAME_SOURCES];

export const DEFAULT_GAME_SOURCES: GameSource[] = [...ALL_GAME_SOURCES];

export const formatGameSourceLabel = (
  source: ExtendedGameSource | string | undefined,
): string => {
  if (source === 'twic' || source === 'master') {
    return 'Master';
  }
  if (source === 'lichess') {
    return 'Lichess Elite';
  }
  if (source === 'lichess_user') {
    return 'Lichess';
  }
  if (source === 'chesscom_user') {
    return 'Chess.com';
  }
  if (source === 'unknown') {
    return '—';
  }
  return source ? String(source) : '—';
};

export const formatGameSourceForUrl = (source: GameSource): string => {
  if (source === 'twic') {
    return 'master';
  }
  if (source === 'lichess') {
    return 'lichess-elite';
  }
  return source;
};

/** Map a corpus path segment (e.g. `/games/browse/master/...`) to internal `GameSource`. */
export const parseCorpusPathSegment = (
  segment: string | null | undefined,
): GameSource | null => {
  const normalized = segment?.trim().toLowerCase();
  if (normalized === 'lichess-elite' || normalized === 'lichess') {
    return 'lichess';
  }
  if (normalized === 'master' || normalized === 'twic') {
    return 'twic';
  }
  return null;
};

export const formatCorpusPathSegment = (source: GameSource): string =>
  formatGameSourceForUrl(source);

export const formatGameSourcesLabel = (sources: GameSource[]): string => {
  if (sources.length === 0 || sources.length === ALL_GAME_SOURCES.length) {
    return ALL_GAME_SOURCES.map(formatGameSourceLabel).join(' & ');
  }
  return sources.map(formatGameSourceLabel).join(' & ');
};

export const parseGameSourcesFromUrl = (
  param: string | null | undefined,
): GameSource[] => {
  if (!param?.trim()) {
    return [...DEFAULT_GAME_SOURCES];
  }

  const parsedSources = param
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .map((part) => {
      if (part === 'lichess' || part === 'lichess-elite') {
        return 'lichess' as const;
      }
      if (part === 'master' || part === 'twic') {
        return 'twic' as const;
      }
      return null;
    })
    .filter((part): part is GameSource => part != null);

  const uniqueSources = [...new Set(parsedSources)];
  return uniqueSources.length > 0 ? uniqueSources : [...DEFAULT_GAME_SOURCES];
};

export const parseGameSourceFromUrl = (
  param: string | null | undefined,
): GameSource => {
  const normalized = param?.trim().toLowerCase();
  if (normalized === 'lichess') {
    return 'lichess';
  }
  if (normalized === 'master' || normalized === 'twic') {
    return 'twic';
  }
  return 'twic';
};

export const filterByGameSources = <T extends { source?: GameSource }>(
  items: T[],
  sources: GameSource[],
): T[] => {
  if (sources.length === 0 || sources.length === ALL_GAME_SOURCES.length) {
    return items;
  }

  return items.filter((item) => item.source && sources.includes(item.source));
};

export const toggleGameSource = (
  selected: GameSource[],
  source: GameSource,
): GameSource[] => {
  if (selected.includes(source)) {
    if (selected.length === 1) {
      return selected;
    }
    return selected.filter((value) => value !== source);
  }

  return [...selected, source];
};
